// BrainBrowser bootstrap: dynamically loads the /brainbrowser/* scripts, wires
// window.BrainBrowser globals, registers the custom intensity worker, and returns
// a ready SurfaceViewer. Extracted verbatim from NeuroViewer.jsx.
export const initBrainBrowser = async () => {
    /**
     * Initializes BrainBrowser viewer and loads required dependencies
     * @returns {Promise} Resolves with BrainBrowser viewer instance
     */
    const loadScript = (url) => {
        return new Promise((resolve, reject) => {
            try {
                if (document.querySelector(`script[src="${url}"]`)) return resolve();
                const script = document.createElement('script');
                script.src = url;
                script.onload = resolve;
                script.onerror = (e) => reject(new Error(`Failed to load script: ${url}`));
                document.head.appendChild(script);
            } catch (error) {
                reject(error);
            }
        });
    };

    try {
        console.log("Initializing BrainBrowser...");

        const scripts = [];

        // First, load the core BrainBrowser object to establish the namespace
        scripts.push("/brainbrowser/brainbrowser.js");

        // Load core modules needed by BrainBrowser
        scripts.push("/brainbrowser/core/tree-store.js");
        scripts.push("/brainbrowser/lib/config.js");
        scripts.push("/brainbrowser/lib/utils.js");
        scripts.push("/brainbrowser/lib/events.js");
        scripts.push("/brainbrowser/lib/loader.js");

        // Create the SurfaceViewer object (but don't start it yet)
        scripts.push("/brainbrowser/surface-viewer.js");

        // Load all scripts sequentially
        for (const script of scripts) {
            console.log(`Loading script: ${script}`);
            await loadScript(script);
        }

        console.log("Verifying BrainBrowser is loaded...");
        if (typeof window.BrainBrowser === 'undefined' || !window.BrainBrowser.SurfaceViewer) {
            throw new Error("BrainBrowser or SurfaceViewer not available");
        }

        // Initialize SurfaceViewer.THREE as an empty object if it doesn't exist
        if (!window.BrainBrowser.SurfaceViewer.THREE) {
            window.BrainBrowser.SurfaceViewer.THREE = {};
        }

        // Now load the custom THREE.js which expects BrainBrowser.SurfaceViewer to exist
        console.log("Loading custom THREE.js...");
        await loadScript("/brainbrowser/surface-viewer/lib/three.js");

        // Verify THREE is now attached to BrainBrowser.SurfaceViewer
        if (!window.BrainBrowser.SurfaceViewer.THREE || !window.BrainBrowser.SurfaceViewer.THREE.REVISION) {
            console.error("Custom THREE.js not properly loaded");
        } else {
            console.log("Custom THREE.js loaded, revision:", window.BrainBrowser.SurfaceViewer.THREE.REVISION);
        }

        // Manually load the surface-viewer modules which depend on THREE.js
        console.log("Loading surface-viewer modules...");
        await Promise.all([
            loadScript("/brainbrowser/surface-viewer/modules/rendering.js"),
            loadScript("/brainbrowser/surface-viewer/modules/loading.js"),
            loadScript("/brainbrowser/surface-viewer/modules/color.js"),
            loadScript("/brainbrowser/surface-viewer/modules/views.js"),
            loadScript("/brainbrowser/surface-viewer/modules/annotations.js")
        ]);
        console.log("Surface-viewer modules loaded.");

        window.BrainBrowser.SurfaceViewer.parseIntensityData = function (data, type, callback) {
            var worker = new Worker(window.BrainBrowser.config.get("worker_dir") + "/" +
                window.BrainBrowser.config.get("intensity_data_types." + type + ".worker"));

            worker.addEventListener("message", function (event) {
                var intensity_data = event.data;

                callback(intensity_data);
                worker.terminate();
            });

            worker.addEventListener("error", function (error) {
                callback({
                    error: true,
                    error_message: error.message || "Error parsing intensity data."
                });
                worker.terminate();
            });

            // URL is needed for the gifti.worker.js to load gifti-reader.js
            worker.postMessage({
                data: data,
                url: window.location.origin + "/brainbrowser/workers/"
            });
        };

        if (!window.BrainBrowser.createColorMap) {
            window.BrainBrowser.createColorMap = function (data) {
                return {
                    colors: data.split("\n").map(function (line) {
                        return line.trim().split(/\s+/).map(parseFloat);
                    }),
                    mapColors: function (values, options) {
                        options = options || {};
                        var min = options.min === undefined ? 0 : options.min;
                        var max = options.max === undefined ? 255 : options.max;
                        var default_colors = options.default_colors || [0, 0, 0, 1];
                        var color_map = this.colors;

                        // Create a Float32Array for the result colors
                        var colors = new Float32Array(values.length * 4);

                        for (var i = 0; i < values.length; i++) {
                            var value = values[i];
                            var color_index;

                            // Map value to color_map index
                            if (value < min) {
                                color_index = 0;
                            } else if (value > max) {
                                color_index = color_map.length - 1;
                            } else {
                                color_index = Math.floor((value - min) / (max - min) * (color_map.length - 1));
                            }

                            var color = color_map[color_index] || default_colors;

                            // Set the color in the result array
                            colors[i * 4] = color[0];
                            colors[i * 4 + 1] = color[1];
                            colors[i * 4 + 2] = color[2];
                            colors[i * 4 + 3] = color[3] === undefined ? 1 : color[3];
                        }

                        return colors;
                    },
                    colorFromValue: function (value, options) {
                        options = options || {};
                        var min = options.min === undefined ? 0 : options.min;
                        var max = options.max === undefined ? 255 : options.max;
                        var color_map = this.colors;
                        var color_index;

                        // Map value to color_map index
                        if (value < min) {
                            color_index = 0;
                        } else if (value > max) {
                            color_index = color_map.length - 1;
                        } else {
                            color_index = Math.floor((value - min) / (max - min) * (color_map.length - 1));
                        }

                        var color = color_map[color_index] || [0, 0, 0, 1];

                        if (options.hex) {
                            return rgbToHex(Math.floor(color[0] * 255),
                                Math.floor(color[1] * 255),
                                Math.floor(color[2] * 255));
                        } else {
                            return color;
                        }
                    }
                };
            };

            // Helper function to convert RGB to hex
            function rgbToHex(r, g, b) {
                return ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
            }
        }

        // At this point, THREE should be available through BrainBrowser.SurfaceViewer.THREE
        // If not, we'll try to use a fallback
        if (!window.BrainBrowser.SurfaceViewer.THREE || !window.BrainBrowser.SurfaceViewer.THREE.WebGLRenderer) {
            console.error("THREE.js not properly initialized. Missing required components.");

            // Try loading a fallback version
            console.log("Attempting to load THREE.js from CDN as fallback...");
            try {
                await loadScript("https://cdnjs.cloudflare.com/ajax/libs/three.js/r69/three.min.js");

                if (window.THREE) {
                    console.log("Successfully loaded THREE.js from CDN fallback");
                    window.BrainBrowser.SurfaceViewer.THREE = window.THREE;
                }
            } catch (error) {
                console.error("Failed to load THREE.js from CDN:", error);
                throw new Error("Could not initialize THREE.js properly");
            }
        }

        console.log("THREE.js configured successfully");

        // Configure BrainBrowser
        if (!window.BrainBrowser.config) {
            window.BrainBrowser.config = {};
        }

        // Ensure the config has set and get methods
        if (typeof window.BrainBrowser.config.set !== 'function') {
            window.BrainBrowser.config.set = function (key, value) {
                this[key] = value;
            };
        }

        if (typeof window.BrainBrowser.config.get !== 'function') {
            window.BrainBrowser.config.get = function (key) {
                return this[key];
            };
        }

        // Set the worker directory to the local path (/public/brainbrowser/)
        window.BrainBrowser.config.set("worker_dir", "/brainbrowser/workers");

        // Configure model types
        window.BrainBrowser.config.set("model_types", {
            mniobj: { worker: "mniobj.worker.js" },
            wavefrontobj: { worker: "wavefrontobj.worker.js" },
            freesurferasc: { worker: "freesurferasc.worker.js" },
            freesurferbin: { worker: "freesurferbin.worker.js" },
            json: { worker: "json.worker.js" }
        });

        // Configure intensity data types
        window.BrainBrowser.config.set("intensity_data_types", {
            freesurferasc: { worker: "freesurferasc.intensity.worker.js" },
            freesurferbin: { worker: "freesurferbin.intensity.worker.js" },
            text: { worker: "text.intensity.worker.js" },
            gifti: { worker: "gifti.worker.js" }
        });

        // Configure color maps
        window.BrainBrowser.config.set("color_maps", [
            { name: "Spectral", url: "/brainbrowser/examples/color-maps/spectral.txt" },
            { name: "Thermal", url: "/brainbrowser/examples/color-maps/thermal.txt" },
            { name: "Gray", url: "/brainbrowser/examples/color-maps/gray-scale.txt" },
            { name: "Blue", url: "/brainbrowser/examples/color-maps/blue.txt" },
            { name: "Green", url: "/brainbrowser/examples/color-maps/green.txt" }
        ]);

        console.log("BrainBrowser configured successfully");

        // Check if the container exists
        const container = document.getElementById("brainbrowser");
        if (!container) {
            throw new Error("BrainBrowser container not found");
        }

        // Register the surface-viewer modules manually
        if (window.BrainBrowser && window.BrainBrowser.SurfaceViewer) {
            console.log("Registering surface-viewer modules...");

            // Ensure THREE.js is available to SurfaceViewer
            if (window.THREE) {
                window.BrainBrowser.SurfaceViewer.THREE = window.THREE;
            }

            // Define the SurfaceViewer modules if not already defined
            if (!window.BrainBrowser.SurfaceViewer.modules) {
                window.BrainBrowser.SurfaceViewer.modules = {};
            }

            // Manually set up the module functions if they've been loaded
            if (window.SurfaceViewer && window.SurfaceViewer.modules) {
                Object.keys(window.SurfaceViewer.modules).forEach(function (key) {
                    window.BrainBrowser.SurfaceViewer.modules[key] = window.SurfaceViewer.modules[key];
                });
                console.log("Surface-viewer modules registered.");
            }
        }

        return new Promise((resolve, reject) => {
            try {
                console.log("Starting BrainBrowser Surface Viewer...");

                window.BrainBrowser.SurfaceViewer.start("brainbrowser", function (viewer) {
                    if (!viewer) {
                        reject(new Error("BrainBrowser viewer initialization failed"));
                        return;
                    }

                    console.log("BrainBrowser viewer created successfully");

                    // Initial render
                    viewer.render();

                    // Add a small delay to ensure everything is initialized
                    setTimeout(() => {
                        console.log("BrainBrowser initialization complete");
                        resolve(viewer);
                    }, 100);
                });
            } catch (error) {
                console.error("Error starting BrainBrowser:", error);
                reject(error);
            }
        });
    } catch (error) {
        console.error("Failed to initialize BrainBrowser:", error);
        return null;
    }
};
